import React, { useState, useEffect, useRef, useCallback } from 'react';
import { IContainer } from '../../common/ContainerSchemas';
import { ContainersApiProvider } from '../../providers/ContainersApiProvider';
import { Constants } from '../../common/Constants';
import {
  Combobox,
  Option,
  useId,
  makeStyles,
  ComboboxProps,
} from '@fluentui/react-components';
import './ContainerDropdown.css';

export interface IContainerDropdownProps {
  selectedContainerId: string;
  onContainerChange: (containerId: string) => void;
  initialContainers?: IContainer[];
}

const useStyles = makeStyles({
  loading: {
    textAlign: 'center',
  },
});

const containersApi = ContainersApiProvider.instance;

/**
 * ContainerDropdown Component with pagination support
 *
 * The API provider ensures all responses are in paginated format,
 * handling both paginated and non-paginated backend responses transparently.
 */
export const ContainerDropdown: React.FC<IContainerDropdownProps> = ({
  selectedContainerId,
  onContainerChange,
  initialContainers = [],
}) => {
  const styles = useStyles();
  const comboboxId = useId('container-combobox');

  const [containers, setContainers] = useState<IContainer[]>(initialContainers);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [selectedOption, setSelectedOption] = useState<string[]>([
    selectedContainerId,
  ]);
  const [value, setValue] = useState('');

  const loadingRef = useRef(false);
  const initialLoadDone = useRef(false);
  const listboxRef = useRef<HTMLDivElement>(null);

  /**
   * Load containers with pagination support.
   * The API provider ensures all responses are in paginated format.
   * The "All" container is automatically included in the first page response.
   */
  const loadContainers = useCallback(async (pageNumber: number) => {
    if (loadingRef.current) return;

    loadingRef.current = true;
    setIsLoading(true);
    try {
      const response = await containersApi.listPaginated(
        pageNumber,
        parseInt(Constants.VITE_APP_CONTAINER_PAGE_SIZE),
      );
      const totalPages = Math.ceil(response.total_count / response.page_size);

      if (pageNumber === 1) {
        // First page - replace all containers (includes "All" at the end)
        setContainers(response.containers);
        setHasMore(response.page < totalPages);
      } else {
        // Subsequent pages - append before "All" container
        setContainers((prev) => {
          const withoutAll = prev.filter((c) => c.id !== 'All');
          const allContainer = prev.find((c) => c.id === 'All');
          return allContainer
            ? [...withoutAll, ...response.containers, allContainer]
            : [...withoutAll, ...response.containers];
        });
        setHasMore(response.page < totalPages);
      }
    } catch (error) {
      console.error('Failed to load containers:', error);
    } finally {
      setIsLoading(false);
      loadingRef.current = false;
    }
  }, []);

  // Load initial containers if not provided
  useEffect(() => {
    if (initialContainers.length === 0 && !initialLoadDone.current) {
      initialLoadDone.current = true;
      loadContainers(1);
    } else if (initialContainers.length > 0 && !initialLoadDone.current) {
      initialLoadDone.current = true;
    }
  }, [initialContainers, loadContainers]);

  // Update selected option when prop changes
  useEffect(() => {
    setSelectedOption([selectedContainerId]);
    const container = containers.find((c) => c.id === selectedContainerId);
    if (container) {
      setValue(container.name);
    }
  }, [selectedContainerId, containers]);

  // Handle infinite scroll for paginated lists
  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const target = e.currentTarget;
      const scrollPosition = target.scrollTop + target.clientHeight;
      const scrollThreshold = target.scrollHeight * 0.8;

      if (!loadingRef.current && hasMore && scrollPosition >= scrollThreshold) {
        const nextPage = page + 1;
        setPage(nextPage);
        loadContainers(nextPage);
      }
    },
    [page, hasMore, loadContainers],
  );

  const handleOptionSelect: ComboboxProps['onOptionSelect'] = (
    _event,
    data,
  ) => {
    _event.currentTarget.blur();
    _event.stopPropagation();
    if (data.optionValue) {
      onContainerChange(data.optionValue);
      setSelectedOption([data.optionValue]);
      const container = containers.find((c) => c.id === data.optionValue);
      if (container) {
        setValue(container.name);
      }
    }
  };

  return (
    <div style={{ display: 'inline-block', position: 'relative' }}>
      <Combobox
        id={comboboxId}
        className="containerDropdown"
        placeholder="Select container"
        value={value}
        selectedOptions={selectedOption}
        onOptionSelect={handleOptionSelect}
        listbox={{
          ref: listboxRef,
          className: 'containerDropdownListbox',
          onScroll: handleScroll,
        }}
        disabled={isLoading && containers.length === 0}
        data-testid="container-dropdown"
      >
        {containers.length === 0 && !isLoading ? (
          <Option value="" disabled>
            No containers available
          </Option>
        ) : (
          containers.map((container) => (
            <Option
              checkIcon=""
              key={container.id}
              value={container.id}
              className="containerDropdownOption"
            >
              {container.name}
            </Option>
          ))
        )}
        {isLoading && (
          <Option value="" disabled className={styles.loading}>
            Loading...
          </Option>
        )}
      </Combobox>
    </div>
  );
};

export default ContainerDropdown;
